import { ApiClient } from './api-client';
import type { ApiResponse } from '../types';

export interface ParticipantRef {
  userId: string;
  name: string;
  /** Provider business name when this side is the provider, null otherwise. */
  businessName: string | null;
}

export interface ConversationSummary {
  id: string;
  /** Null for inquiry threads opened before any booking. */
  bookingId: string | null;
  /** Null when bookingId is null. */
  bookingStatus: string | null;
  providerId: string | null;
  otherParticipant: ParticipantRef;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface ConversationDetail {
  id: string;
  bookingId: string | null;
  bookingStatus: string | null;
  providerId: string | null;
  customer: ParticipantRef;
  provider: ParticipantRef;
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderUserId: string;
  body: string;
  /** ISO-8601 UTC instant. */
  sentAt: string;
  /** ISO-8601 UTC instant when the recipient first read this message; null until then. */
  readAt: string | null;
}

export const chatService = {
  listMine: (): Promise<ApiResponse<ConversationSummary[]>> =>
    ApiClient.get<ConversationSummary[]>('/me/conversations'),

  /** Total inbound unread messages across all conversations — the header
   *  inbox badge count. */
  unreadCount: async (): Promise<number> =>
    (await ApiClient.get<{ count: number }>('/me/conversations/unread-count')).data.count,

  getDetail: (conversationId: string): Promise<ApiResponse<ConversationDetail>> =>
    ApiClient.get<ConversationDetail>(`/me/conversations/${conversationId}`),

  listMessages: (
    conversationId: string,
    params: { before?: string; size?: number } = {},
  ): Promise<ApiResponse<ChatMessage[]>> =>
    ApiClient.get<ChatMessage[], { before?: string; size?: number }>(
      `/me/conversations/${conversationId}/messages`,
      params,
    ),

  sendMessage: async (conversationId: string, body: string): Promise<ChatMessage> =>
    (await ApiClient.post<ChatMessage>(`/me/conversations/${conversationId}/messages`, { body }))
      .data,

  markRead: async (conversationId: string): Promise<void> => {
    await ApiClient.post(`/me/conversations/${conversationId}/read`);
  },

  /** Open or reuse an inquiry conversation with a provider. Optional `body`
   *  is sent as the first message in the same round-trip. */
  openWithProvider: async (
    providerId: string,
    body?: string,
  ): Promise<ConversationDetail> =>
    (
      await ApiClient.post<ConversationDetail>(
        `/me/conversations/with-provider/${providerId}`,
        { body: body ?? '' },
      )
    ).data,
};
